import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarDays, Clock, MapPin } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { Spinner } from "@/components/common";
import { api, dateRange, errMsg, fmtDate } from "@/lib/api";

export default function Guide() {
  const { slug } = useParams();
  const [g, setG] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/guide/${slug}`).then((r) => setG(r.data)).catch((e) => setErr(errMsg(e))); }, [slug]);
  const days = {};
  (g?.itinerary || []).forEach((i) => { (days[i.date] = days[i.date] || []).push(i); });
  const color = g?.brand_color || "#F97316";
  return (
    <PublicLayout>
      {err ? <div className="mx-auto max-w-xl p-10 text-center" data-testid="guide-error"><h1 className="text-2xl font-bold">Guide not available</h1><p className="mt-2 text-slate-600">{err}</p></div>
        : !g ? <Spinner /> : (
        <div data-testid="public-guide">
          <div className="relative h-72 w-full overflow-hidden sm:h-96" style={{ background: color }}>
            {g.cover_image && <img src={g.cover_image} alt="" className="h-full w-full object-cover opacity-80" />}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
            <div className="absolute bottom-8 left-0 right-0 mx-auto max-w-5xl px-4 text-white">
              {g.brand_tagline && <p className="text-sm font-semibold uppercase tracking-wider">{g.brand_tagline}</p>}
              <h1 className="text-4xl font-extrabold sm:text-5xl">{g.name}</h1>
              <div className="mt-3 flex flex-wrap gap-4 text-sm">
                {g.destination && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{g.destination}</span>}
                <span className="flex items-center gap-1"><CalendarDays className="h-4 w-4" />{dateRange(g.start_date, g.end_date)}</span>
                {g.timezone && <span className="flex items-center gap-1"><Clock className="h-4 w-4" />Times in {g.timezone}</span>}
              </div>
            </div>
          </div>
          <div className="mx-auto grid max-w-5xl gap-8 px-4 py-10 lg:grid-cols-[1fr_2fr]">
            <div className="space-y-4">
              {g.description && <div className="card p-5"><h2 className="font-semibold">About</h2><p className="mt-2 whitespace-pre-line text-sm text-slate-600">{g.description}</p></div>}
              {g.guide_notes && <div className="card p-5"><h2 className="font-semibold">Good to know</h2><p className="mt-2 whitespace-pre-line text-sm text-slate-600">{g.guide_notes}</p></div>}
            </div>
            <div className="space-y-6">
              <h2 className="text-2xl font-bold">Itinerary</h2>
              {Object.keys(days).length === 0 && <p className="text-slate-500">The itinerary hasn't been shared yet.</p>}
              {Object.entries(days).map(([d, items]) => (
                <div key={d}>
                  <p className="mb-2 text-sm font-semibold uppercase tracking-wider" style={{ color }}>{fmtDate(d)}</p>
                  <div className="space-y-2">
                    {items.map((i, k) => (
                      <div key={k} className="card flex gap-4 p-4">
                        <span className="w-20 shrink-0 font-mono text-sm text-slate-500">{i.start_time || "—"}</span>
                        <div><p className="font-semibold">{i.title}{i.optional && <span className="ml-2 text-xs text-slate-500">(optional)</span>}</p>{i.location && <p className="text-sm text-slate-500">{i.location}</p>}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </PublicLayout>
  );
}
